<template>
  <div class="report-page">
    <div class="dropdown-container">
      <!-- FSM -->
      <div class="dropdown-item">
        <label for="fsmSelect">FSM</label>
        <select id="fsmSelect" v-model="selectedFsm" @change="onFsmChange" :disabled="isFsmLocked">
          <option value="">Select FSM</option>
          <option v-if="isOtherUser" :value="ALL">All FSMs</option>
          <option v-for="fsm in fsmList" :key="fsm.profile_id" :value="String(fsm.profile_id)">
            {{ fsm.name }}
          </option>
        </select>
      </div>

      <!-- ASM -->
      <div class="dropdown-item">
        <label for="asmSelect">ASM</label>
        <select id="asmSelect" v-model="selectedAsm" @change="onAsmChange" :disabled="isAsmLocked">
          <option value="">Select ASM</option>
          <option v-if="isFsmUser || isOtherUser" :value="ALL">All ASMs</option>
          <option v-for="asm in asmList" :key="asm.profile_id" :value="String(asm.profile_id)">
            {{ asm.name }}
          </option>
        </select>
      </div>

      <!-- Distributor -->
      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributor" @change="onDistributorChange"
          :disabled="isDistributorLocked">
          <option value="">Select Distributor</option>
          <!-- keep same visibility rules as ASM/FSM-wise -->
          <option v-if="isFsmUser || isAsmUser || isOtherUser" :value="ALL">All Distributors</option>

          <option v-for="distributor in distributorList" :key="distributor.profile_id"
            :value="String(distributor.profile_id)">
            {{ distributor.name }}
          </option>
        </select>
      </div>

      <!-- Dealer -->
      <div class="dropdown-item">
        <label for="dealerSelect">Dealer</label>
        <select id="dealerSelect" v-model="selectedDealer" :class="{ 'inactive-select': selectedDealerIsInactive }">
          <option value="">All Dealers</option>
          <option v-for="dealer in dealerList" :key="dealer.profile_id" :value="String(dealer.profile_id)" :class="{ 'inactive-option': Number(dealer.is_active) === 0 }">
            {{ dealer.name }}
          </option>
        </select>
      </div>

      <div class="dropdown-item">
        <label for="sourceSelect">Sales Source</label>
        <select id="sourceSelect" v-model="selectedSource" @change="onSourceChange">
          <option value="all">All Sales</option>
          <option value="rep">Sales Reps (Mobile)</option>
          <option value="distributor">Distributor (Web)</option>
        </select>
      </div>

      <div class="dropdown-item" v-if="selectedSource !== 'distributor'">
        <label for="repSelect">Rep Name</label>
        <select id="repSelect" v-model="selectedRep">
          <option value="">All Reps</option>
          <option v-for="r in repList" :key="r.profile_id ?? r.id" :value="String(r.profile_id ?? r.id)">
            {{ r.name ?? r.first_name ?? r.full_name ?? ("Rep #" + (r.profile_id ?? r.id)) }}
          </option>
        </select>
      </div>


      <div class="dropdown-item">
        <label>Start Date</label>
        <input type="date" v-model="startDate" :max="currentDate" @change="handleDateChange"
          @blur="validateStartDate(true)" />
      </div>

      <div class="dropdown-item">
        <label>End Date</label>
        <input type="date" v-model="endDate" :max="currentDate" @change="handleDateChange"
          @blur="validateEndDate(true)" />
      </div>



      <div class="search-container">
        <button type="button" @click="performSearch(1)" class="btn button-default" :disabled="loading">
          <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
          {{ loading ? 'Searching...' : 'Search' }}
        </button>

        <button type="button" class="btn button-export" :disabled="!reportData.length || loading" @click="exportToExcel">
          Export to Excel
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <div v-if="!loading && reportData.length" class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <tr>
              <!--<th class="sticky-col">Invoice No</th>-->
              <th class="sticky-col">Distributor</th>
              <th class="sticky-col">Route</th>
              <th class="sticky-col">Dealer Name</th>
              <!--<th class="sticky-col">Invoice Date</th>-->
              <th v-for="variant in productVariants" :key="variant" class="product-col">{{ variant }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in reportData" :key="i" :class="{ 'inactive-row': Number(row.is_active) === 0 }">
              <!--<td class="sticky-col">{{ row['Invoice No'] || '-' }}</td>-->
              <td class="sticky-col">{{ row['Distributor'] || '-' }}</td>
              <td class="sticky-col">{{ row['Route'] || '-' }}</td>
              <td class="sticky-col">{{ row['Dealer Name'] || '-' }}</td>
              <!--<td class="sticky-col">{{ row['Invoice Date'] || '-' }}</td> -->
              <td v-for="variant in productVariants" :key="variant">
                {{ row[variant] !== undefined && row[variant] !== null ? row[variant] : 0 }}
              </td>
            </tr>
            <!-- Total Row -->
            <tr class="total-row">
              <td class="sticky-col" colspan="3"><strong>Total</strong></td>
              <td v-for="variant in productVariants" :key="variant">
                <strong>{{ calculateTotal(variant) }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!loading && !reportData.length" class="no-data-message">
      <p v-if="searched">No data found for the selected filters.</p>
      <p v-else>Please select filters and click Search.</p>
    </div>

    <!-- ✅ NEW: Pagination controls -->
<!-- ✅ NEW: Pagination controls (full version with page size + jump) -->
<div v-if="!loading && reportData.length" class="pagination-bar mt-3">

  <!-- LEFT: Page size selector + row range info -->
  <div class="pagination-left">
    <label class="page-size-label">
  Show
  <select v-model="perPage" @change="onPageSizeChange" :disabled="loading" class="page-size-select">
    <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
  </select>
  per page
</label>
    <span class="row-info">{{ rowRangeText }}</span>
  </div>

  <!-- CENTER: Page navigation buttons -->
  <div class="pagination-center" v-if="totalPages > 1">
    <button class="btn-pagination" @click="firstPage" :disabled="currentPage <= 1 || loading" title="First page">««</button>
    <button class="btn-pagination" @click="prevPage" :disabled="currentPage <= 1 || loading" title="Previous">«</button>

    <button
      v-for="(p, idx) in visiblePageNumbers"
      :key="idx"
      class="btn-pagination"
      :class="{ active: p === currentPage, ellipsis: p === '...' }"
      :disabled="p === '...' || loading"
      @click="goToPage(p)"
    >
      {{ p }}
    </button>

    <button class="btn-pagination" @click="nextPage" :disabled="!hasMore || loading" title="Next">»</button>
    <button class="btn-pagination" @click="lastPage" :disabled="currentPage >= totalPages || loading" title="Last page">»»</button>
  </div>

  <!-- RIGHT: Jump-to-page input -->
  <div class="pagination-right" v-if="totalPages > 5">
    <span>Go to</span>
    <input
      type="number"
      class="jump-input"
      v-model="jumpToPageInput"
      :min="1"
      :max="totalPages"
      :disabled="loading"
      @keyup.enter="handleJumpToPage"
      placeholder="page"
    />
    <button class="btn-pagination btn-go" @click="handleJumpToPage" :disabled="loading">Go</button>
  </div>
</div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchDealerListManagementTrue,
  fetchSalesReps,
  fetchDisdeaReport,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

const EXCLUDE_KEYS = [
  "Distributor", "Route", "Dealer Name", "Rep Name",
  "Vehicle No", "Invoice Date", "Invoice No", "is_active",
];

export default {
  data() {
    const currentDate = new Date().toISOString().split("T")[0];
    const ALL = "__all__";

    return {
      token: "",
      fsmList: [],
      asmList: [],
      distributorList: [],
      dealerList: [],

      // ✅ NEW lists
      repList: [],

      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",
      selectedDealer: "",
      selectedSource: "all",

      // ✅ NEW selected
      selectedRep: "",

      startDate: currentDate,
      endDate: currentDate,
      currentDate,

      loading: false,
      searched: false,
reportData: [],
productVariants: [],

// ✅ NEW: pagination state
currentPage: 1,
perPage: 20,
totalRows: 0,
totalPages: 0,
hasMore: false,

// ✅ NEW: allowed page sizes for the dropdown
pageSizeOptions: [20, 50, 100],

// ✅ NEW: jump-to-page input value
jumpToPageInput: '',

// ✅ NEW: totals from backend (across ALL data, not just current page)
columnTotals: {},
grandTotal: 0,

// ✅ NEW: track if user is exporting (so we know to fetch all rows)
isExporting: false,

      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      isFsmLocked: false,
      isAsmLocked: false,
      isDistributorLocked: false,

      ALL,
      redirecting: false,
    };

  },

  computed: {
  isDistributorUser() { return this.roleKey === "DIS"; },
  isAsmUser() { return this.roleKey === "ASM"; },
  isFsmUser() { return this.roleKey === "FSM"; },
  isOtherUser() { return this.roleKey === "OTHER"; },

  grandTotalAllRows() {
    return Number(this.grandTotal || 0);
  },

  // ✅ NEW: visible page numbers (max 7) with ellipsis for huge ranges
  //   e.g.  [1, ..., 4, 5, 6, ..., 50]   or   [1, 2, 3, 4, 5, ..., 50]
  visiblePageNumbers() {
    const total = this.totalPages;
    const current = this.currentPage;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages = [];
    pages.push(1);

    let start = Math.max(2, current - 2);
    let end = Math.min(total - 1, current + 2);

    if (current <= 4) { start = 2; end = 5; }
    if (current >= total - 3) { start = total - 4; end = total - 1; }

    if (start > 2) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('...');

    pages.push(total);
    return pages;
  },

  // ✅ NEW: "Showing 21-40 of 5,433 rows"
  rowRangeText() {
    if (!this.totalRows) return '';
    const start = (this.currentPage - 1) * this.perPage + 1;
    const end = Math.min(this.currentPage * this.perPage, this.totalRows);
    return `Showing ${start}-${end} of ${this.totalRows.toLocaleString()} rows`;
  },

  selectedDealerIsInactive() {
    const d = this.dealerList.find(
      (x) => String(x.profile_id) === String(this.selectedDealer),
    );
    return !!d && Number(d.is_active) === 0;
  },
},
  mounted() {
    this.initLoginAndLoadFsm();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    bodyOf(x) { return x?.data ?? x; },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },

    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();

      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      // ✅ Only logout on 401 if message is truly auth-related
      if (http === 401) return authMsg.test(msg);

      return (
        http === 419 ||
        http === 440 ||
        http === 498 ||
        authMsg.test(msg)
      );
    },

    redirectToLogin(message) {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", message || "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },

    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },



    // same role detection as working report
    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const labelUpper = this.userLabel.toUpperCase().trim();

      if (
        labelUpper === "DISTRIBUTOR" ||
        labelUpper.startsWith("DISTRIBUTOR") ||
        labelUpper === "DIS" ||
        labelUpper.startsWith("DIS_")
      ) this.roleKey = "DIS";
      else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) this.roleKey = "ASM";
      else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) this.roleKey = "FSM";
      else this.roleKey = "OTHER";
    },

    // init like ASM/FSM-wise (use existing session token)
    async initLoginAndLoadFsm() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token_set") || "";
        // if (!token) {
        //   this.toast("error", "Session expired. Please login again.");
        //   return;
        // }
        if (!token) {
          this.redirectToLogin("Session expired. Please login again.");
          return;
        }


        this.token = token;
        this.myProfileId = Number(localStorage.getItem("profile_id") || 0);
        this.initRoleFromStorage();

        await this.loadFsmList();

        // reset filter state
        this.selectedFsm = "";
        this.selectedAsm = "";
        this.selectedDistributor = "";
        this.selectedDealer = "";

        this.selectedRep = "";

        this.asmList = [];
        this.distributorList = [];
        this.dealerList = [];
        this.isDistributorLocked = false;

      } catch (err) {
        console.error("[DealerSales] init error:", err?.response?.data || err);
        this.toast("error", "Failed to initialize page.");
      } finally {
        this.loading = false;
      }
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;

      // block manual formats like 2025/11/31
      if (dateStr.includes("/")) return false;

      // must be YYYY-MM-DD
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;

      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);

      // detect overflow dates like 2025-11-31
      const dt = new Date(y, mo - 1, d);
      return (
        dt.getFullYear() === y &&
        dt.getMonth() === mo - 1 &&
        dt.getDate() === d
      );
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true;

      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.toast("error", "Invalid Start Date");
        this.startDate = "";
        return false;
      }

      if (this.startDate > this.currentDate) {
        if (showToast) this.toast("error", "Start Date cannot be in the future");
        this.startDate = "";
        return false;
      }

      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true;

      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.toast("error", "Invalid End Date");
        this.endDate = "";
        return false;
      }

      if (this.endDate > this.currentDate) {
        if (showToast) this.toast("error", "End Date cannot be in the future");
        this.endDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      // validate each field silently first
      this.validateStartDate(false);
      this.validateEndDate(false);

      // validate range if both exist
      if (this.startDate && this.endDate) {
        if (this.endDate < this.startDate) {
          this.toast("error", "End date cannot be before start date.");
          this.endDate = "";
        }
      }
    },

    validateDatesForSearch() {
      // REQUIRED
      if (!this.startDate) { this.toast("error", "Please select valid Start Date"); return false; }
      if (!this.endDate) { this.toast("error", "Please select valid End Date"); return false; }

      // VALID
      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;

      // RANGE
      if (this.endDate < this.startDate) {
        this.toast("error", "End date cannot be before start date.");
        return false;
      }

      return true;
    },




    async loadFsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchFsmList(profileId, this.token);

        if (this.handleUnauthorized(res)) return;
        const body = this.bodyOf(res);
        if (this.handleUnauthorized(body)) return;

        this.fsmList = body?.data || [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        this.toast("error", "Failed to load FSM list.");
      }
    },


    async onFsmChange() {
      this.selectedAsm = "";
      this.selectedDistributor = "";
      this.selectedDealer = "";
      this.selectedRep = "";

      this.asmList = [];
      this.distributorList = [];
      this.dealerList = [];
      this.repList = [];

      this.isDistributorLocked = false;

      // clear report
      this.reportData = [];
      this.currentPage = 1;
this.totalPages = 0;
this.totalRows = 0;
this.columnTotals = {};
this.grandTotal = 0;
      this.productVariants = [];

      if (this.selectedFsm === this.ALL) return;
      if (!this.selectedFsm) return;

      await this.loadAsmList();
    },

    async onAsmChange() {
      this.selectedDistributor = "";
      this.selectedDealer = "";
      this.selectedRep = "";

      this.distributorList = [];
      this.dealerList = [];
      this.repList = [];

      this.isDistributorLocked = false;

      // clear report
      this.reportData = [];
      this.currentPage = 1;
this.totalPages = 0;
this.totalRows = 0;
this.columnTotals = {};
this.grandTotal = 0;
      this.productVariants = [];

      if (this.selectedAsm === this.ALL) return;
      if (!this.selectedAsm) return;

      await this.loadDistributorList();
    },

    async onDistributorChange() {
      this.selectedDealer = "";
      this.selectedRep = "";

      this.dealerList = [];
      this.repList = [];

      // clear report
      this.reportData = [];
      this.currentPage = 1;
this.totalPages = 0;
this.totalRows = 0;
this.columnTotals = {};
this.grandTotal = 0;
      this.productVariants = [];

      if (this.selectedDistributor === this.ALL) return;
      if (!this.selectedDistributor) return;

      // ✅ load dealer list + vehicle list + rep list
      await this.loadDealerList();
      await this.loadRepList();
    },

    async onDealerChange() {
      // keep selections but reset report
      this.reportData = [];
      this.productVariants = [];
    },



    async loadAsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchAsmList(profileId, this.selectedFsm, this.token);

        if (this.handleUnauthorized(res)) return;
        const body = this.bodyOf(res);
        if (this.handleUnauthorized(body)) return;

        const list = body?.data || [];
        this.asmList = list;

        if (!list.length) {


          this.selectedAsm = "";
          this.selectedDistributor = "";
          this.selectedDealer = "";
          this.selectedRep = "";

          this.distributorList = [];
          this.dealerList = [];
          this.repList = [];
          this.isDistributorLocked = false;

          this.reportData = [];
          this.productVariants = [];
        }
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        this.toast("error", "Failed to load ASM list.");
      }
    },



    //   try {
    //     const profileId = localStorage.getItem("profile_id");
    //     const res = await fetchDistributorList(profileId, this.selectedAsm, this.token);
    //     let list = res?.data?.data || [];

    //     if (this.isDistributorUser && this.myProfileId) {
    //       const mine = list.find(d => Number(d.profile_id) === Number(this.myProfileId));
    //       if (mine) {
    //         list = [mine];
    //         this.selectedDistributor = String(mine.profile_id);
    //         this.isDistributorLocked = true;

    //         // programmatic set doesn't trigger @change
    //         await this.loadDealerList();
    //         await this.loadRepList();
    //       }
    //     }

    //     this.distributorList = list;
    //   } catch (err) {
    //     console.error(err);
    //     this.toast("error", "Failed to load Distributor list.");
    //   }
    // },

    async loadDistributorList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchDistributorList(profileId, this.selectedAsm, this.token);

        if (this.handleUnauthorized(res)) return;
        const body = this.bodyOf(res);
        if (this.handleUnauthorized(body)) return;

        let list = body?.data || [];

        if (!list.length) {

          this.selectedDistributor = "";
          this.selectedDealer = "";
          this.selectedRep = "";

          this.distributorList = [];
          this.dealerList = [];
          this.repList = [];
          this.isDistributorLocked = false;

          this.reportData = [];
          this.productVariants = [];
          return;
        }

        // Distributor login: lock to own distributor
        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find(d => Number(d.profile_id) === Number(this.myProfileId));
          if (mine) {
            list = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;

            await this.loadDealerList();
            await this.loadRepList();
          }
        }

        this.distributorList = list;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        this.toast("error", "Failed to load Distributor list.");
      }
    },


    async loadDealerList() {
      this.dealerList = [];
      this.selectedDealer = "";

      if (!this.selectedDistributor || this.selectedDistributor === this.ALL) return;

      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchDealerListManagementTrue(profileId, this.selectedDistributor, true, this.token);
        if (this.handleUnauthorized(res)) return;
        const body = this.bodyOf(res);
        if (this.handleUnauthorized(body)) return;


        this.dealerList = res?.data?.data || [];
      } catch (err) {
        console.error(err);
        this.toast("error", "Failed to load Dealer list.");
      }
    },


    // async loadRepList() {
    //   this.repList = [];
    //   this.selectedRep = "";

    //   try {
    //     const profileId = localStorage.getItem("profile_id");
    //     const res = await fetchSalesRepList(profileId, this.token);
    //     if (this.handleUnauthorized(res)) return;
    //     const body = this.bodyOf(res);
    //     if (this.handleUnauthorized(body)) return;


    //     this.repList = res?.data?.data || res?.data || [];
    //   } catch (err) {
    //     console.error(err);
    //     this.toast("error", "Failed to load Rep list.");
    //   }
    // },

    async loadRepList() {
      this.repList = [];
      this.selectedRep = "";

      // must have a specific distributor
      if (!this.selectedDistributor || this.selectedDistributor === this.ALL) return;

      try {
        const profileId = localStorage.getItem("profile_id");
        const distributorId = this.selectedDistributor;

        // ✅ IMPORTANT: use distributorId-aware API
        const apiRes = await fetchSalesReps(profileId, distributorId, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        // depending on your backend shape
        const list = body?.data ?? body?.data?.data ?? [];
        this.repList = Array.isArray(list) ? list : [];

        // optional UX
        // if (!this.repList.length) this.toast("error", "No reps available for this distributor");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load Rep list.");
      }
    },


    validateFiltersOneByOne() {
      if (!this.selectedFsm) { this.toast("error", "Please select FSM."); return false; }
      if (!this.selectedAsm) { this.toast("error", "Please select ASM."); return false; }
      if (!this.selectedDistributor) { this.toast("error", "Please select Distributor."); return false; }

      if (this.isDistributorUser && this.selectedDistributor === this.ALL) {
        this.toast("error", "Please select Distributor.");
        return false;
      }

      return true;
    },

    // ✅ Row total (sum of all product variant cols)
    rowTotal(row) {
      return this.productVariants.reduce((sum, v) => {
        const val = row?.[v];
        const n = val !== undefined && val !== null ? Number(val) : 0;
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
    },


    // ✅ keep your original total calc



async performSearch(pageToLoad = 1) {
  // ✅ same UX as ASM/FSM-wise sales report
  if (!this.validateFiltersOneByOne()) return;

  // ✅ always refresh token (same pattern as your other report)
  this.token = localStorage.getItem("token_set") || "";
  if (!this.token) {
    if (this.redirectToLogin) return this.redirectToLogin("Session expired. Please login again.");
    this.toast("error", "Session expired. Please login again.");
    return;
  }

  if (!this.validateDatesForSearch()) return;

  this.loading = true;
  // ✅ Don't clear table while loading other pages (smoother UX)
  if (pageToLoad === 1) {
    this.resetReport();
    this.searched = false;
  }
  this.currentPage = pageToLoad;

      try {
        const payload = {
  start_date: this.startDate,
  end_date: this.endDate,

  // ✅ ALL => null (backend should treat null as "no filter")
  fsm_profile_id: this.selectedFsm === this.ALL ? null : this.selectedFsm,
  asm_profile_id: this.selectedAsm === this.ALL ? null : this.selectedAsm,
  distributor_profile_id:
    this.selectedDistributor === this.ALL ? null : this.selectedDistributor,

  // Dealer: keep your existing behavior ("" means All Dealers)
  ...(this.selectedDealer ? { dealer_profile_id: this.selectedDealer } : {}),
  ...(this.selectedRep ? { rep_profile_id: this.selectedRep } : {}),
  source: this.selectedSource,

  // ✅ NEW: pagination params
page: pageToLoad,
per_page: this.isExporting ? 10000 : this.perPage,

// ✅ NEW: skip totals on page 2+ (we already have them)
skip_totals: pageToLoad > 1,
};

        const apiRes = await fetchDisdeaReport(this.token, payload);

        // ✅ NEW: unauthorized checks (axios response + body)
        if (this.handleUnauthorized?.(apiRes)) return;
        const res = this.bodyOf ? this.bodyOf(apiRes) : (apiRes?.data ?? apiRes);
        if (this.handleUnauthorized?.(res)) return;

        // ✅ success check (support both shapes: {status, report} OR {status, data:{report}})
        const ok = res?.status === true || res?.status === 1;
        const report = Array.isArray(res?.report)
          ? res.report
          : Array.isArray(res?.data?.report)
            ? res.data.report
            : [];
            

        if (!ok) {
          if (pageToLoad === 1) { this.resetReport(); this.searched = true; }
          this.toast("error", res?.message || "Error loading report.");
          return;
        }

        if (!report.length) {
          this.resetReport();
          this.searched = true;
          this.toast("error", "No data found for the selected filters.");
          return;
        }

// ✅ Freeze the data (faster Vue rendering for large arrays)
this.reportData = Object.freeze(this.groupByDealer(report));

// ✅ NEW: read pagination + totals from response
const pagination = res?.pagination || {};
this.currentPage = pagination.current_page || 1;
this.totalRows = pagination.total || 0;
this.totalPages = pagination.total_pages || 0;
this.hasMore = pagination.has_more || false;

// ✅ Only overwrite totals if backend sent fresh ones (page 1 only)
if (res?.column_totals && Object.keys(res.column_totals).length > 0) {
  this.columnTotals = res.column_totals;
  this.grandTotal = Number(res.grand_total || 0);
}

const firstRow = this.reportData[0] || {};
this.productVariants = Object.keys(firstRow)
  .filter((k) => !EXCLUDE_KEYS.includes(k))
  .sort();

this.toast("success", `Page ${this.currentPage} of ${this.totalPages} loaded.`);
      } catch (err) {
        // ✅ NEW: unauthorized check for thrown axios errors
        if (this.handleUnauthorized?.(err)) return;

        console.error("Error loading report:", err?.response?.data || err);
        if (pageToLoad === 1) { this.resetReport(); this.searched = true; }
        this.toast("error", err?.response?.data?.message || "Error loading report.");
      } finally {
        this.loading = false;
      }
    },


    calculateTotal(variant) {
  // ✅ Use backend column_totals (covers ALL pages, not just current page)
  return Number(this.columnTotals[variant] || 0);
},

    // ✅ keep your original export
    async exportToExcel() {
      if (!this.totalRows) {
        this.toast("error", "No data to export.");
        return;
      }

      // fetch ALL rows first
      this.isExporting = true;
      this.toast("success", "Preparing export, please wait...");
      try {
        await this.performSearch(1);
      } finally {
        this.isExporting = false;
      }
      if (!this.reportData.length) {
        this.toast("error", "Export failed - no data returned.");
        return;
      }

      // ===== FILTER SECTION =====
      const filterData = [
        ["Distributor Dealer Sales Report"],
        [],
        ["FSM", this.getName(this.fsmList, this.selectedFsm)],
        ["ASM", this.getName(this.asmList, this.selectedAsm)],
        ["Distributor", this.getName(this.distributorList, this.selectedDistributor)],
        ["Dealer", this.getName(this.dealerList, this.selectedDealer) || "All Dealers"],
        ["Rep Name", this.getName(this.repList, this.selectedRep) || "All Reps"],
        ["Sales Source", { all: "All Sales", rep: "Sales Reps", distributor: "Distributor" }[this.selectedSource]],
        ["Start Date", this.startDate],
        ["End Date", this.endDate],
        [],
      ];

      // ===== TABLE DATA =====
      const exportData = this.reportData.map(row => {
        const baseData = {
          "Distributor": row.Distributor || "-",
          "Route": row.Route || "-",
          "Dealer Name": row["Dealer Name"] || "-",
        };
        this.productVariants.forEach(variant => {
          baseData[variant] = row[variant] ?? 0;
        });
        return baseData;
      });

      const totalRow = { "Distributor": "Total", "Route": "", "Dealer Name": "" };
      this.productVariants.forEach(variant => {
        totalRow[variant] = this.calculateTotal(variant);
      });
      exportData.push(totalRow);

      // Convert to worksheet
      const ws = XLSX.utils.aoa_to_sheet(filterData);

      // add table starting row 11
      XLSX.utils.sheet_add_json(ws, exportData, { origin: "A11" });

      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const generatedBy = `${firstName} ${lastName}`.trim();
      const generatedAt = new Date().toLocaleString();

      const generatedRows = [
        [],
        ["Generated By", generatedBy],
        ["Generated At", generatedAt],
      ];

      // Calculate row position after table
      const tableEndRow = 11 + exportData.length + 1;

      XLSX.utils.sheet_add_aoa(ws, generatedRows, { origin: `A${tableEndRow}` });

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, ws, "Sales Report");
      XLSX.writeFile(workbook, `Sales_Report_${this.startDate}_to_${this.endDate}.xlsx`);
      this.toast("success", "Excel file exported successfully.");
      await this.performSearch(1);
    },

    // ✅ NEW: pagination handlers
// ✅ NEW: pagination handlers
goToPage(pageNum) {
  // ignore ellipsis or invalid input
  if (typeof pageNum !== 'number') return;
  if (pageNum < 1 || pageNum > this.totalPages) return;
  if (pageNum === this.currentPage || this.loading) return;
  this.performSearch(pageNum);
},

nextPage() {
  this.goToPage(this.currentPage + 1);
},

prevPage() {
  this.goToPage(this.currentPage - 1);
},

// ✅ NEW: First / Last shortcuts
firstPage() {
  this.goToPage(1);
},

lastPage() {
  this.goToPage(this.totalPages);
},

// ✅ NEW: Page size dropdown handler
onPageSizeChange() {
  // ✅ Force number type (in case select returns string)
  this.perPage = Number(this.perPage) || 20;
  
  console.log("Page size changed to:", this.perPage); // for debugging
  
  if (!this.reportData.length) {
    console.log("No data — skipping reload");
    return;
  }
  
  // Reset to page 1 when page size changes
  this.performSearch(1);
},
// ✅ NEW: Jump to specific page (from input box)
handleJumpToPage() {
  console.log("Go button clicked. Input value:", this.jumpToPageInput);
  
  const raw = String(this.jumpToPageInput || '').trim();
  if (raw === '') {
    this.toast("error", "Please enter a page number.");
    return;
  }
  
  const num = parseInt(raw, 10);
  if (!Number.isFinite(num) || isNaN(num)) {
    this.toast("error", "Please enter a valid page number.");
    return;
  }
  if (num < 1 || num > this.totalPages) {
    this.toast("error", `Page must be between 1 and ${this.totalPages}.`);
    return;
  }
  if (num === this.currentPage) {
    this.toast("error", `You're already on page ${num}.`);
    return;
  }
  
  this.jumpToPageInput = '';
  this.performSearch(num);
},

    getName(list, id) {
      if (!id || id === this.ALL) return "All";
      const item = list.find(i => String(i.profile_id) === String(id));
      return item ? item.name : "";
    },

    groupByDealer(rows) {
      const map = new Map();
      rows.forEach(r => {
        const key = r["Dealer Name"] || "-";
        if (!map.has(key)) {
          map.set(key, {
            "Distributor": r["Distributor"],
            "Route": r["Route"],
            "Dealer Name": r["Dealer Name"],
            is_active: r.is_active,
          });
        }
        const g = map.get(key);
        Object.keys(r).forEach(k => {
          if (EXCLUDE_KEYS.includes(k)) return;
          g[k] = (Number(g[k]) || 0) + (Number(r[k]) || 0);
        });
      });
      return Array.from(map.values());
    },

    resetReport() {
      this.reportData = [];
      this.productVariants = [];
      this.columnTotals = {};
      this.grandTotal = 0;
      this.currentPage = 1;
      this.totalRows = 0;
      this.totalPages = 0;
      this.hasMore = false;
    },

    onSourceChange() {
      if (this.selectedSource === "distributor") this.selectedRep = "";
      this.resetReport();
      this.searched = false;
    },
  },
};
</script>




<style scoped>
.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
}

.table-scroll table {
  min-width: 100%;
}

.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-export:hover {
  background-color: #218838;
  transform: scale(1.05);
}

.btn.button-export:active {
  transform: scale(0.95);
}

.btn.button-export:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: calc(100vh - 20px);
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.container .search-container .btn {
  height: 40px;
  padding: 0 18px;
  margin: 0 !important;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  cursor: pointer;
  transform: none;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

/* DISABLED */
.container .search-container .btn:disabled,
.container .search-container .btn.button-default:disabled,
.container .search-container .btn.button-export:disabled {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  color: #ffffff !important;
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
}

/* FOCUS */
.container .search-container .btn:focus-visible {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

.card {
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.table th {
  background-color: #f9f9f9;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
}

.table td {
  background-color: #ffffff;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: #f8f9fa;
}

.total-row {
  background-color: #e9ecef !important;
  font-weight: bold;
  border-top: 2px solid #007bff;
}

.total-row td {
  background-color: #e9ecef !important;
  font-size: 15px;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}


/* push the whole table layer below any dropdowns/menus */
.card,
.table-scroll {
  position: relative;
  z-index: 0 !important;
}

/* OVERRIDE your existing ".table th { z-index: 10; }" */
.table-scroll .table thead th {
  z-index: 0 !important;
  /* key fix */
}

/* if you have sticky columns, keep header col slightly above body col */
.table-scroll .table th.sticky-col {
  z-index: 1 !important;
}

.table-scroll .table td.sticky-col {
  z-index: 0 !important;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-top: 12px;
}

.page-info {
  font-size: 14px;
  color: #333;
}

.row-info {
  color: #888;
  margin-left: 8px;
}

.btn-pagination {
  padding: 6px 14px;
  border: 1px solid #007bff;
  background: #ffffff;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.btn-pagination:hover:not(:disabled) {
  background: #007bff;
  color: #ffffff;
}

.btn-pagination:disabled {
  border-color: #ccc;
  color: #ccc;
  cursor: not-allowed;
}

.pagination-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-top: 12px;
  border: 1px solid #e1e4e8;
}

.pagination-left,
.pagination-center,
.pagination-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.pagination-center {
  flex: 1;
  justify-content: center;
}

.page-size-label {
  font-size: 13px;
  color: #333;
  margin: 0;
  font-weight: normal;
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-size-select {
  width: auto !important;      /* override global select 100% */
  min-width: 70px;
  padding: 5px 8px !important; /* override global padding */
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  background: #ffffff;
  cursor: pointer;
}

.page-size-select:disabled {
  background: #f0f0f0;
  cursor: not-allowed;
}

.row-info {
  color: #666;
  font-size: 13px;
  margin-left: 10px;
}

.btn-pagination {
  min-width: 34px;
  padding: 5px 10px;
  border: 1px solid #007bff;
  background: #ffffff;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  line-height: 1.4;
}

.btn-pagination:hover:not(:disabled):not(.ellipsis) {
  background: #007bff;
  color: #ffffff;
}

.btn-pagination.active {
  background: #007bff;
  color: #ffffff;
  font-weight: 600;
  cursor: default;
}

.btn-pagination.ellipsis {
  border: none;
  cursor: default;
  background: transparent;
  color: #888;
  padding: 5px 4px;
}

.btn-pagination:disabled:not(.active) {
  border-color: #ddd;
  color: #bbb;
  cursor: not-allowed;
  background: #fafafa;
}

.jump-input {
  width: 70px !important;        /* override global input 100% */
  padding: 5px 8px !important;   /* override global padding */
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
}

.jump-input:disabled {
  background: #f0f0f0;
}

.btn-go {
  min-width: auto;
  padding: 5px 12px;
}

.pagination-right {
  font-size: 13px;
  color: #333;
}

/* normal options always look normal */
.dropdown-item select option {
  background-color: #ffffff;
  color: #212529;
}

/* Inactive dealer: dropdown options */
.dropdown-item select option.inactive-option {
  background-color: #ffebee;
}

/* Select box when an inactive dealer is the selected value */
.dropdown-item select.inactive-select {
  background-color: #ffebee;
}

/* Inactive dealer: table rows */
.table.table-striped > tbody > tr.inactive-row > td {
  --bs-table-accent-bg: transparent;
  --bs-table-bg-type: transparent;
  box-shadow: none !important;
  background-color: #ffebee !important;
}

@media (max-width: 1000px) {
  .dropdown-container { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .container .search-container { grid-column: span 3; }
}

@media (max-width: 800px) {
  .dropdown-container { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .container .search-container { grid-column: span 2; }
}

@media (max-width: 480px) {
  .dropdown-container { grid-template-columns: minmax(0, 1fr); }
  .container .search-container {
    grid-column: span 1;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .container .search-container .btn { width: 100%; min-width: 0; padding: 0 10px; }
}

/* Mobile responsive */
@media (max-width: 768px) {
  .pagination-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .pagination-left,
  .pagination-center,
  .pagination-right {
    justify-content: center;
  }
}
</style>